import { Injectable } from "@angular/core";
import gql from "graphql-tag";
import { first } from "rxjs/operators";

import { isNotNullOrUndefined, removeAllMaskIdFromDeviceList } from "../utils";
import { GraphqlApiService } from "../graphql-api/graphql-api.service";
import { BuildDeviceFwInfoQuery, BuildDevicesStatusInfoQuery } from "../queries/query-constructors.gql";
import { Inverter, InverterV2 } from "../telemetry";
import { Device } from "../models/firebase.model";
import { MonitoringType, ProductModelService } from "../models";

@Injectable({
  providedIn: 'root'
})
export class DeviceDetailService {
  readonly client$ = this.graphqlApiService.client$.pipe(isNotNullOrUndefined());

  constructor(
    private readonly graphqlApiService: GraphqlApiService,
  ) {}

  async fetchDeviceFwInfo(deviceId: string, gwType: MonitoringType, refresh: boolean = false): Promise<Partial<Inverter> | undefined> {
    const client = await this.client$.pipe(first()).toPromise();
    const fetchPolicy = refresh ? 'network-only' : 'cache-first';
    const query = gql`${BuildDeviceFwInfoQuery(deviceId, gwType)}`;
    const { data } = await client.query<{ [key: string ]: Array<Partial<InverterV2>> }>({ query, fetchPolicy });
    const cleanData = removeAllMaskIdFromDeviceList(data);
    return cleanData[deviceId].length ? cleanData[deviceId][0] : undefined;
  }

  async fetchDevicesStatusInfo(serials: string[], gwType: MonitoringType, refresh: boolean = false): Promise<{ [key: string ]: Partial<Inverter> }> {
    const client = await this.client$.pipe(first()).toPromise();
    const fetchPolicy = refresh ? 'network-only' : 'cache-first';

    let emsId = '';
    const devices: Device[] = serials.map((serial) => {
      const product = new ProductModelService(serial);
      const type = product.type?.name ?? 'inverter';
      if (type === 'connext' || type === 'azzurro-hub') {
        emsId = serial;
        return { id: `ZZ${serial.toUpperCase()}Z`, type, name: serial };
      }
      return { id: serial, type, name: serial };
    }).filter((device) => ["inverter" , "meter" , "connext" , "enerclic", "azzurro-hub"].includes(device.type));

    if (devices.length === 0) {
      return {};
    }

    const query = gql`${BuildDevicesStatusInfoQuery(devices, gwType)}`;
    const { data } = await client.query<{ [key: string ]: Partial<Inverter>[] }>({ query, fetchPolicy });
    const rawTelemetry: { [key: string]: Partial<Inverter> } = {};

    Object.keys(data).forEach((key) => {
      if (data[key] && data[key][0] !== null && data[key][0] !== undefined) {
        rawTelemetry[key] = data[key][0];
      } else {
        rawTelemetry[key] = {};
      }
    });

    if (emsId && rawTelemetry[`ZZ${emsId.toUpperCase()}Z`]) {
      const temp = rawTelemetry[`ZZ${emsId.toUpperCase()}Z`];
      rawTelemetry[emsId] = temp;
      delete rawTelemetry[`ZZ${emsId.toUpperCase()}Z`];
    }

    return removeAllMaskIdFromDeviceList(rawTelemetry);
  }
}
